import { useState, useEffect, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { User, CheckSquare, Square, Loader2, Building2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { ViewToggle, type ViewMode } from "@/components/common/ViewToggle";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useSettings } from "@/providers/SettingsProvider";

const STATUS_OPTIONS = ["new", "looking", "open", "unavailable", "hired"] as const;

interface SearchResultsListProps {
	results: any;
	jobId: string;
	onAddMatches?: (candidates: string[]) => void;
	onClose?: () => void;
	flexibilityLevel?: 'strict' | 'normal' | 'flexible';
	onFlexibilityChange?: (level: 'strict' | 'normal' | 'flexible') => void;
}

export function SearchResultsList({ results, jobId, onAddMatches, onClose, flexibilityLevel = 'normal', onFlexibilityChange }: SearchResultsListProps) {
	const { t } = useTranslation();
	const { advancedSearchViewMode, setAdvancedSearchViewModeValue } = useSettings();
	const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(new Set());
	const [isAdding, setIsAdding] = useState(false);
	const [candidateSyntheses, setCandidateSyntheses] = useState<Record<string, string>>({});
	const [candidateStatuses, setCandidateStatuses] = useState<Record<string, string>>({});
	const [benchCandidateIds, setBenchCandidateIds] = useState<Set<string>>(new Set());

	// Filter state
	const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set(["new", "open", "looking"]));
	const [benchOnly, setBenchOnly] = useState(false);
	const viewMode = advancedSearchViewMode as ViewMode;
	const setViewMode = (mode: ViewMode) => setAdvancedSearchViewModeValue(mode);

	useEffect(() => {
		if (results?.shortlist) {
			fetchCandidateData();
		}
	}, [results]);

	const fetchCandidateData = async () => {
		if (!results?.shortlist) return;

		const candidateIds = results.shortlist.map((c: any) => c.candidateId);

		// Fetch syntheses and statuses
		const { data: candidateData, error: candidateError } = await supabase
			.from('candidates')
			.select('id, ai_synthesis, status')
			.in('id', candidateIds);

		if (!candidateError && candidateData) {
			const synthesesMap: Record<string, string> = {};
			const statusesMap: Record<string, string> = {};
			candidateData.forEach(c => {
				synthesesMap[c.id] = c.ai_synthesis || '';
				statusesMap[c.id] = c.status || '';
			});
			setCandidateSyntheses(synthesesMap);
			setCandidateStatuses(statusesMap);
		}

		// Fetch bench assignments
		const { data: benchData, error: benchError } = await supabase
			.from('workforce')
			.select('candidate_id')
			.in('candidate_id', candidateIds)
			.eq('is_bench', true);

		if (!benchError && benchData) {
			setBenchCandidateIds(new Set(benchData.map(w => w.candidate_id)));
		}
	};

	if (!results || !results.shortlist)
		return null;

	const toggleSelection = (candidateId: string, isAlreadyMatched: boolean) => {
		if (isAlreadyMatched) return;

		const newSelection = new Set(selectedCandidates);
		if (newSelection.has(candidateId))
			newSelection.delete(candidateId);
		else
			newSelection.add(candidateId);
		setSelectedCandidates(newSelection);
	};

	useEffect(() => {
		if (results?.shortlist) {
			const matchedIds = new Set<string>(
				results.shortlist
					.filter((c: any) => c.isAlreadyMatched)
					.map((c: any) => c.candidateId)
			);

			setSelectedCandidates(prev => {
				const updated = new Set(prev);
				matchedIds.forEach(id => updated.delete(id));
				return updated;
			});
		}
	}, [results]);

	const toggleStatusFilter = (status: string) => {
		setStatusFilter(prev => {
			const next = new Set(prev);
			if (next.has(status))
				next.delete(status);
			else
				next.add(status);
			return next;
		});
	};

	// Filter results
	const filteredResults = useMemo(() => {
		return results.shortlist.filter((candidate: any) => {
			const status = candidateStatuses[candidate.candidateId];

			// Status filter: if any statuses selected, candidate must match one
			if (statusFilter.size > 0 && status && !statusFilter.has(status))
				return false;

			// Bench filter
			if (benchOnly && !benchCandidateIds.has(candidate.candidateId))
				return false;

			return true;
		});
	}, [results.shortlist, candidateStatuses, benchCandidateIds, statusFilter, benchOnly]);

	const handleAddSelected = async () => {
		if (selectedCandidates.size === 0) return;

		setIsAdding(true);
		try {
			const candidateIds = Array.from(selectedCandidates);
			const matchInserts = candidateIds.map(candidateId => ({
				job_id: jobId,
				candidate_id: candidateId,
				score: 0,
				status: 'matched'
			}));

			const { data: existingMatches, error: checkError } = await supabase
				.from('matches')
				.select('candidate_id')
				.eq('job_id', jobId)
				.in('candidate_id', candidateIds);

			if (checkError) throw checkError;

			const existingIds = new Set(existingMatches?.map(m => m.candidate_id) || []);
			const newMatchInserts = matchInserts.filter(m => !existingIds.has(m.candidate_id));

			if (newMatchInserts.length === 0) {
				toast({
					title: t("advancedSearch.toasts.info"),
					description: t("advancedSearch.toasts.allAlreadyMatched"),
				});
				setSelectedCandidates(new Set());
				return;
			}

			const { data, error } = await supabase
				.from('matches')
				.insert(newMatchInserts)
				.select('id');

			if (error) throw error;

			for (const match of data) {
				await supabase.functions.invoke('ai-processing?command=push-new-analysis', {
					method: 'POST',
					body: {
						entityId: match.id,
						entityType: "match",
						analysisType: "full-match",
						actionOnComplete: "update-match",
					}
				});
			}

			const skippedCount = candidateIds.length - newMatchInserts.length;

			toast({
				title: t("advancedSearch.toasts.success"),
				description: skippedCount > 0
					? `${t("advancedSearch.toasts.added")} ${newMatchInserts.length} ${t("advancedSearch.toasts.candidates")}. ${t("advancedSearch.toasts.alreadyMatchedCount", { count: skippedCount })}`
					: `${t("advancedSearch.toasts.added")} ${newMatchInserts.length} ${t("advancedSearch.toasts.candidates")}`,
			});

			setSelectedCandidates(new Set());
			onAddMatches?.(candidateIds);

			if (onClose) {
				setTimeout(() => {
					onClose();
				}, 500);
			}
		} catch (error) {
			console.error('Error adding matches:', error);
			toast({
				title: t("errors.error"),
				description: t("advancedSearch.toasts.addError"),
				variant: "destructive",
			});
		} finally {
			setIsAdding(false);
		}
	};

	const getStatusBadgeClasses = (status: string) => {
		switch (status) {
			case 'looking': return 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800';
			case 'open': return 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800';
			case 'new': return 'bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800';
			case 'unavailable': return 'bg-gray-50 dark:bg-gray-950/30 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-800';
			case 'hired': return 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800';
			default: return '';
		}
	};

	const renderCandidateCard = (candidate: any) => {
		const isAlreadyMatched = candidate.isAlreadyMatched || false;
		const status = candidateStatuses[candidate.candidateId];
		const isBench = benchCandidateIds.has(candidate.candidateId);

		const matchedSkills = candidate.skillComparison?.filter((s: any) => s.matched) || [];
		const similarSkills = candidate.skillComparison
			? Array.from(new Map(
				candidate.skillComparison
					.filter((s: any) => !s.matched && s.similarity > 0.5)
					.map((s: any) => [s.jobSkillId, s])
			).values()) as any[]
			: [];
		const similarExperiences = candidate.experienceComparison
			? Array.from(new Map(
				candidate.experienceComparison
					.filter((e: any) => !e.matched && e.similarity > 0.5)
					.map((e: any) => [e.jobExperienceId, e])
			).values()) as any[]
			: [];

		if (viewMode === 'card') {
			return (
				<Card
					key={candidate.candidateId}
					className={`p-4 transition-all border-border dark:border-border flex flex-col ${
						isAlreadyMatched
							? 'opacity-50 cursor-not-allowed bg-muted/30'
							: 'hover:shadow-md cursor-pointer hover:border-blue-300 dark:hover:border-blue-700'
					}`}
					onClick={() => !isAlreadyMatched && toggleSelection(candidate.candidateId, isAlreadyMatched)}
				>
					{/* Header row */}
					<div className="flex items-start justify-between gap-2 mb-2">
						<div className="flex items-center gap-2">
							<div className="pt-0.5 flex-shrink-0">
								{isAlreadyMatched ? (
									<Tooltip>
										<TooltipTrigger asChild>
											<div><CheckSquare className="h-4 w-4 text-emerald-600" /></div>
										</TooltipTrigger>
										<TooltipContent side="top">
											<p>{t("advancedSearch.results.alreadyMatchedTooltip")}</p>
										</TooltipContent>
									</Tooltip>
								) : selectedCandidates.has(candidate.candidateId) ? (
									<CheckSquare className="h-4 w-4 text-blue-500" />
								) : (
									<Square className="h-4 w-4 text-muted-foreground/40" />
								)}
							</div>
							<div className="h-9 w-9 rounded-sm bg-blue-500 flex items-center justify-center flex-shrink-0">
								<User className="h-4 w-4 text-white" />
							</div>
						</div>
						<div className="flex items-center gap-1.5 flex-shrink-0">
							{isAlreadyMatched && (
								<Badge variant="outline" className="text-[10px] h-5 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700">
									{t("advancedSearch.results.alreadyMatched")}
								</Badge>
							)}
							{isBench && (
								<Badge variant="outline" className="text-[10px] h-5 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 border-orange-300 dark:border-orange-700">
									<Building2 className="h-2.5 w-2.5 mr-0.5" />
									{t("advancedSearch.results.bench")}
								</Badge>
							)}
							{candidate.combinedScore >= 85 && (
								<Badge
									variant="outline"
									className={`text-[10px] h-5 ${
										candidate.combinedScore >= 150
											? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700"
											: "bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700"
									}`}
								>
									{candidate.combinedScore >= 150 ? t("advancedSearch.results.highConfidence") : t("advancedSearch.results.strongConfidence")}
								</Badge>
							)}
						</div>
					</div>

					{/* Name & function */}
					<div className="mb-2">
						<h4
							className="font-medium text-sm text-blue-500 dark:text-blue-400 hover:underline"
							onClick={(e) => {
								e.stopPropagation();
								window.open(`/candidates/${candidate.candidateId}`, '_blank');
							}}
						>
							{candidate.candidateName}
						</h4>
						<div className="flex items-center gap-1.5 mt-0.5">
							<span className="text-xs text-muted-foreground truncate">{candidate.candidateFunction}</span>
							{status && (
								<Badge variant="outline" className={`text-[10px] h-5 ${getStatusBadgeClasses(status)}`}>
									{t(`candidates.status.${status}`, { defaultValue: status })}
								</Badge>
							)}
						</div>
					</div>

					{/* All tags with tooltips */}
					<div className="flex flex-wrap gap-1 mb-2 flex-1">
						{matchedSkills.map((skill: any, idx: number) => (
							<Badge key={`m-${idx}`} variant="outline" className="text-[10px] h-5 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800">
								{skill.jobSkillName}
							</Badge>
						))}
						{similarSkills.map((skill: any, idx: number) => (
							<Tooltip key={`s-${idx}`}>
								<TooltipTrigger asChild>
									<span onClick={(e) => e.stopPropagation()}>
										<Badge variant="outline" className="text-[10px] h-5 bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800 cursor-help">
											{skill.jobSkillName}
										</Badge>
									</span>
								</TooltipTrigger>
								<TooltipContent side="top" className="z-50">
									<p className="text-xs">{t("advancedSearch.results.candidateHas", { name: skill.candidateSkillName })}</p>
									<p className="text-[10px] mt-0.5 text-muted-foreground">{t("advancedSearch.results.similarity", { percent: Math.round(skill.similarity * 100) })}</p>
								</TooltipContent>
							</Tooltip>
						))}
						{similarExperiences.map((exp: any, idx: number) => (
							<Tooltip key={`e-${idx}`}>
								<TooltipTrigger asChild>
									<span onClick={(e) => e.stopPropagation()}>
										<Badge variant="outline" className="text-[10px] h-5 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 cursor-help">
											{exp.jobExperienceName}
										</Badge>
									</span>
								</TooltipTrigger>
								<TooltipContent side="top" className="z-50">
									<p className="text-xs">{t("advancedSearch.results.candidateHas", { name: exp.candidateExperienceName })}</p>
									<p className="text-[10px] mt-0.5 text-muted-foreground">{t("advancedSearch.results.similarity", { percent: Math.round(exp.similarity * 100) })}</p>
								</TooltipContent>
							</Tooltip>
						))}
					</div>

					{/* AI Synthesis */}
					{candidateSyntheses[candidate.candidateId] && (
						<p className="text-xs text-muted-foreground mt-auto">
							{candidateSyntheses[candidate.candidateId]}
						</p>
					)}

					{/* Match Reasons */}
					{candidate.matchReasons && candidate.matchReasons.length > 0 && (
						<div className="mt-1.5 flex flex-wrap gap-1">
							{candidate.matchReasons.map((reason: string, idx: number) => (
								<Badge key={idx} variant="secondary" className="text-[10px] h-5">
									{reason}
								</Badge>
							))}
						</div>
					)}
				</Card>
			);
		}

		// List view
		return (
			<Card
				key={candidate.candidateId}
				className={`p-3 transition-all border-border dark:border-border ${
					isAlreadyMatched
						? 'opacity-50 cursor-not-allowed bg-muted/30'
						: 'hover:shadow-md cursor-pointer hover:border-blue-300 dark:hover:border-blue-700'
				}`}
				onClick={() => !isAlreadyMatched && toggleSelection(candidate.candidateId, isAlreadyMatched)}
			>
				<div className="flex items-start gap-3">
					{/* Checkbox */}
					<div className="pt-0.5 flex-shrink-0">
						{isAlreadyMatched ? (
							<Tooltip>
								<TooltipTrigger asChild>
									<div><CheckSquare className="h-4 w-4 text-emerald-600" /></div>
								</TooltipTrigger>
								<TooltipContent side="top">
									<p>{t("advancedSearch.results.alreadyMatchedTooltip")}</p>
								</TooltipContent>
							</Tooltip>
						) : selectedCandidates.has(candidate.candidateId) ? (
							<CheckSquare className="h-4 w-4 text-blue-500" />
						) : (
							<Square className="h-4 w-4 text-muted-foreground/40" />
						)}
					</div>

					{/* Avatar */}
					<div className="h-10 w-10 rounded-sm bg-blue-500 flex items-center justify-center flex-shrink-0">
						<User className="h-5 w-5 text-white" />
					</div>

					{/* Content */}
					<div className="flex-1 min-w-0">
						<div className="flex items-center justify-between gap-2 mb-1">
							<div className="flex items-center gap-2 min-w-0">
								<h4
									className="font-medium text-sm text-blue-500 dark:text-blue-400 hover:underline truncate"
									onClick={(e) => {
										e.stopPropagation();
										window.open(`/candidates/${candidate.candidateId}`, '_blank');
									}}
								>
									{candidate.candidateName}
								</h4>
								<span className="text-xs text-muted-foreground truncate">{candidate.candidateFunction}</span>
								{status && (
									<Badge variant="outline" className={`text-[10px] h-5 ${getStatusBadgeClasses(status)}`}>
										{t(`candidates.status.${status}`, { defaultValue: status })}
									</Badge>
								)}
								{isBench && (
									<Badge variant="outline" className="text-[10px] h-5 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 border-orange-300 dark:border-orange-700">
										<Building2 className="h-2.5 w-2.5 mr-0.5" />
										{t("advancedSearch.results.bench")}
									</Badge>
								)}
							</div>
							<div className="flex items-center gap-1.5 flex-shrink-0">
								{isAlreadyMatched && (
									<Badge variant="outline" className="text-[10px] h-5 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700">
										{t("advancedSearch.results.alreadyMatched")}
									</Badge>
								)}
								{candidate.combinedScore >= 85 && (
									<Badge
										variant="outline"
										className={`text-[10px] h-5 ${
											candidate.combinedScore >= 150
												? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700"
												: "bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700"
										}`}
									>
										{candidate.combinedScore >= 150 ? t("advancedSearch.results.highConfidence") : t("advancedSearch.results.strongConfidence")}
									</Badge>
								)}
							</div>
						</div>

						{/* Skills & Experiences inline */}
						<div className="flex flex-wrap gap-1 mb-1.5">
							{matchedSkills.map((skill: any, idx: number) => (
								<Badge key={`m-${idx}`} variant="outline" className="text-[10px] h-5 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800">
									{skill.jobSkillName}
								</Badge>
							))}
							{similarSkills.map((skill: any, idx: number) => (
								<Tooltip key={`s-${idx}`}>
									<TooltipTrigger asChild>
										<span onClick={(e) => e.stopPropagation()}>
											<Badge variant="outline" className="text-[10px] h-5 bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800 cursor-help">
												{skill.jobSkillName}
											</Badge>
										</span>
									</TooltipTrigger>
									<TooltipContent side="top" className="z-50">
										<p className="text-xs">{t("advancedSearch.results.candidateHas", { name: skill.candidateSkillName })}</p>
										<p className="text-[10px] mt-0.5 text-muted-foreground">{t("advancedSearch.results.similarity", { percent: Math.round(skill.similarity * 100) })}</p>
									</TooltipContent>
								</Tooltip>
							))}
							{similarExperiences.map((exp: any, idx: number) => (
								<Tooltip key={`e-${idx}`}>
									<TooltipTrigger asChild>
										<span onClick={(e) => e.stopPropagation()}>
											<Badge variant="outline" className="text-[10px] h-5 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 cursor-help">
												{exp.jobExperienceName}
											</Badge>
										</span>
									</TooltipTrigger>
									<TooltipContent side="top" className="z-50">
										<p className="text-xs">{t("advancedSearch.results.candidateHas", { name: exp.candidateExperienceName })}</p>
										<p className="text-[10px] mt-0.5 text-muted-foreground">{t("advancedSearch.results.similarity", { percent: Math.round(exp.similarity * 100) })}</p>
									</TooltipContent>
								</Tooltip>
							))}
						</div>

						{/* AI Synthesis */}
						{candidateSyntheses[candidate.candidateId] && (
							<p className="text-xs text-muted-foreground">
								{candidateSyntheses[candidate.candidateId]}
							</p>
						)}

						{/* Match Reasons */}
						{candidate.matchReasons && candidate.matchReasons.length > 0 && (
							<div className="mt-1.5 flex flex-wrap gap-1">
								{candidate.matchReasons.map((reason: string, idx: number) => (
									<Badge key={idx} variant="secondary" className="text-[10px] h-5">
										{reason}
									</Badge>
								))}
							</div>
						)}
					</div>
				</div>
			</Card>
		);
	};

	return (
		<div>
			{/* Sticky filter & action bar */}
			<div className="sticky -top-4 z-10 bg-background border-b border-border pb-3 mb-3 space-y-3 pt-4">
				{/* Action row */}
				<div className="flex items-center justify-between">
					<p className="text-sm text-muted-foreground">
						{t("advancedSearch.results.foundCount", { count: filteredResults.length })}
						{filteredResults.length !== results.shortlist.length && (
							<span className="text-muted-foreground/60"> / {results.shortlist.length} {t("advancedSearch.results.total")}</span>
						)}
					</p>
					<div className="flex items-center gap-3">
						<ViewToggle
							viewMode={viewMode}
							onViewModeChange={setViewMode}
							label=""
							allowedModes={["card", "list"]}
						/>
						<Button
							onClick={handleAddSelected}
							disabled={selectedCandidates.size === 0 || isAdding}
							className="bg-blue-500 hover:bg-blue-500 text-white"
							size="sm"
						>
							{isAdding ? (
								<>
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
									{t("advancedSearch.results.adding")}
								</>
							) : (
								t("advancedSearch.results.addSelected", { count: selectedCandidates.size })
							)}
						</Button>
					</div>
				</div>

				{/* Filter row */}
				<div className="flex items-center gap-4 flex-wrap">
					{/* Status multi-select as clickable badges */}
					<div className="flex items-center gap-1.5">
						<span className="text-xs text-muted-foreground mr-1">{t("advancedSearch.results.filterStatus")}:</span>
						{STATUS_OPTIONS.map(status => (
							<Badge
								key={status}
								variant="outline"
								className={`text-[10px] h-5 cursor-pointer select-none transition-all ${
									statusFilter.has(status)
										? getStatusBadgeClasses(status) + ' ring-1 ring-offset-1 ring-blue-400'
										: 'opacity-40 hover:opacity-70'
								}`}
								onClick={() => toggleStatusFilter(status)}
							>
								{t(`candidates.status.${status}`, { defaultValue: status })}
							</Badge>
						))}
					</div>

					<div className="h-4 w-px bg-border" />

					{/* Bench checkbox */}
					<div className="flex items-center gap-1.5">
						<Checkbox
							id="bench-filter"
							checked={benchOnly}
							onCheckedChange={(checked) => setBenchOnly(checked === true)}
							className="h-3.5 w-3.5"
						/>
						<Label htmlFor="bench-filter" className="text-xs text-muted-foreground cursor-pointer">
							{t("advancedSearch.results.benchOnly")}
						</Label>
					</div>

					{onFlexibilityChange && (
						<>
							<div className="h-4 w-px bg-border" />
							<div className="flex items-center gap-1.5">
								<span className="text-xs text-muted-foreground mr-0.5">{t("advancedSearch.flexibility.label")}:</span>
								<div className="flex items-center gap-0.5 border border-border rounded p-0.5">
									{(['strict', 'normal', 'flexible'] as const).map(level => (
										<button
											key={level}
											onClick={() => onFlexibilityChange(level)}
											className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
												flexibilityLevel === level
													? 'bg-blue-500 text-white'
													: 'text-muted-foreground hover:text-foreground'
											}`}
										>
											{t(`advancedSearch.flexibility.${level}`)}
										</button>
									))}
								</div>
							</div>
						</>
					)}
				</div>
			</div>

			<TooltipProvider delayDuration={300}>
				{viewMode === 'list' ? (
					<div className="space-y-2">
						{filteredResults.map((candidate: any) => renderCandidateCard(candidate))}
					</div>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
						{filteredResults.map((candidate: any) => renderCandidateCard(candidate))}
					</div>
				)}

				{filteredResults.length === 0 && (
					<div className="text-center py-8 text-muted-foreground">
						<p className="text-sm">{t("advancedSearch.results.noResultsFiltered")}</p>
					</div>
				)}
			</TooltipProvider>
		</div>
	);
}
